import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function AuthPage() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState('auth'); // 'auth', 'kyc'

  const handleLogin = (e) => {
    e.preventDefault();
    localStorage.setItem('britedge_auth', 'true');
    navigate('/dashboard');
  };

  const handleSignup = (e) => {
    e.preventDefault();
    setStep('kyc');
  };

  const handleKYC = (e) => {
    e.preventDefault();
    localStorage.setItem('britedge_auth', 'true');
    navigate('/dashboard');
  };

  if (step === 'kyc') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Button 
            variant="ghost" 
            onClick={() => setStep('auth')}
            data-testid="kyc-back-btn"
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="bg-white rounded-3xl shadow-xl p-8 space-y-6">
            <div className="text-center space-y-2">
              <h2 className="text-3xl font-bold font-heading text-slate-900">Complete Your Profile</h2>
              <p className="text-slate-600">We need a few details to verify your identity</p>
            </div>

            <form onSubmit={handleKYC} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bvn">Bank Verification Number (BVN)</Label>
                <Input 
                  id="bvn" 
                  type="text" 
                  placeholder="22345678901"
                  data-testid="kyc-bvn-input"
                  required
                  className="h-12 rounded-lg"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="nin">National Identity Number (NIN)</Label>
                <Input 
                  id="nin" 
                  type="text" 
                  placeholder="12345678901"
                  data-testid="kyc-nin-input"
                  className="h-12 rounded-lg"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Residential Address</Label>
                <Input 
                  id="address" 
                  type="text" 
                  placeholder="123 Lagos Street, Ikeja"
                  data-testid="kyc-address-input"
                  required
                  className="h-12 rounded-lg"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dob">Date of Birth</Label>
                <Input 
                  id="dob" 
                  type="date" 
                  data-testid="kyc-dob-input"
                  required
                  className="h-12 rounded-lg"
                />
              </div>

              <Button 
                type="submit" 
                data-testid="kyc-submit-btn"
                className="w-full bg-brand-blue text-white hover:bg-blue-700 rounded-full h-12 text-lg font-semibold shadow-lg hover:shadow-blue-500/25 transition-all active:scale-95"
              >
                Complete Verification
              </Button>
            </form>

            <p className="text-xs text-center text-slate-500">
              Your information is encrypted and secure. We comply with CBN regulations.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/')}
          data-testid="auth-back-btn"
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>
        
        <div className="bg-white rounded-3xl shadow-xl p-8 space-y-6">
          <div className="text-center space-y-2">
            <img 
              src="https://britedgeng.com/images/logo.png" 
              alt="Britedge" 
              className="h-12 mx-auto mb-4"
            />
            <h2 className="text-3xl font-bold font-heading text-slate-900">Welcome to Britedge</h2>
            <p className="text-slate-600">Your financial edge starts here</p>
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="tab-login">Login</TabsTrigger>
              <TabsTrigger value="signup" data-testid="tab-signup">Sign Up</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="space-y-4">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email or Phone</Label>
                  <Input 
                    id="login-email" 
                    type="text" 
                    placeholder="your@email.com or +234..."
                    data-testid="login-email-input"
                    required
                    className="h-12 rounded-lg"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <div className="relative">
                    <Input 
                      id="login-password" 
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      data-testid="login-password-input"
                      required
                      className="h-12 rounded-lg pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="rounded" />
                    <span className="text-slate-600">Remember me</span>
                  </label>
                  <a href="#" className="text-brand-blue hover:underline font-medium">
                    Forgot password?
                  </a>
                </div>

                <Button 
                  type="submit" 
                  data-testid="login-submit-btn"
                  className="w-full bg-brand-blue text-white hover:bg-blue-700 rounded-full h-12 text-lg font-semibold shadow-lg hover:shadow-blue-500/25 transition-all active:scale-95"
                >
                  Login
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup" className="space-y-4">
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-firstname">First Name</Label>
                    <Input 
                      id="signup-firstname" 
                      type="text" 
                      placeholder="Adewale"
                      data-testid="signup-firstname-input"
                      required
                      className="h-12 rounded-lg"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-lastname">Last Name</Label>
                    <Input 
                      id="signup-lastname" 
                      type="text" 
                      placeholder="Johnson"
                      data-testid="signup-lastname-input"
                      required
                      className="h-12 rounded-lg"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email Address</Label>
                  <Input 
                    id="signup-email" 
                    type="email" 
                    placeholder="your@email.com"
                    data-testid="signup-email-input"
                    required
                    className="h-12 rounded-lg"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-phone">Phone Number</Label>
                  <Input 
                    id="signup-phone" 
                    type="tel" 
                    placeholder="+234 801 234 5678"
                    data-testid="signup-phone-input"
                    required
                    className="h-12 rounded-lg"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <div className="relative">
                    <Input 
                      id="signup-password" 
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Create a strong password"
                      data-testid="signup-password-input"
                      required
                      className="h-12 rounded-lg pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <input 
                    type="checkbox" 
                    id="terms" 
                    required 
                    className="mt-1 rounded"
                    data-testid="signup-terms-checkbox"
                  />
                  <label htmlFor="terms" className="text-sm text-slate-600">
                    I agree to Britedge's <a href="#" className="text-brand-blue hover:underline">Terms of Service</a> and <a href="#" className="text-brand-blue hover:underline">Privacy Policy</a>
                  </label>
                </div>

                <Button 
                  type="submit" 
                  data-testid="signup-submit-btn"
                  className="w-full bg-brand-blue text-white hover:bg-blue-700 rounded-full h-12 text-lg font-semibold shadow-lg hover:shadow-blue-500/25 transition-all active:scale-95"
                >
                  Create Account
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-slate-500">Or continue with</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Button 
              variant="outline" 
              className="h-12 rounded-lg"
              data-testid="auth-google-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Google
            </Button>
            <Button 
              variant="outline" 
              className="h-12 rounded-lg"
              data-testid="auth-facebook-btn"
            >
              <svg className="w-5 h-5 mr-2" fill="#1877F2" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Facebook
            </Button>
          </div>
        </div>

        <p className="mt-6 text-center text-sm text-slate-600">
          Secure authentication powered by 256-bit encryption
        </p>
      </div>
    </div>
  );
}
